// The facade product code calls. Stamps + redacts entries, hands them to the store,
// installs global error hooks, and produces the downloadable .log file.
import { redact } from './redact';
import { buildFile } from './format';
import * as store from './log-store';
import { installGlobalErrorHandlers } from './global-errors';
import { collectSystemInfo } from './system-info';
import type { SystemInfo } from './system-info';
import { environment } from '../../config/environment';
import { useUserStore } from '../../stores/user-store';
import type { LogCategory, LogEntry, LogLevel } from './types';

const RETENTION_MS = 30 * 60 * 1000;
const PRUNE_INTERVAL_MS = 10 * 60 * 1000;
let initialized = false;

// Injected by Vite's `define` from package.json (see vite.config.ts). Guarded so
// non-Vite contexts (e.g. a bare unit test) don't throw a ReferenceError.
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';

function write(level: LogLevel, category: LogCategory, message: string, data?: unknown): void {
  try {
    const entry: LogEntry = { ts: Date.now(), level, category, message };
    if (data !== undefined) entry.data = redact(data);
    store.append(entry);
  } catch {
    // logging must never throw into product code
  }
}

function fileStamp(d: Date): string {
  const p = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

export const logger = {
  info: (category: LogCategory, message: string, data?: unknown) => write('info', category, message, data),
  warn: (category: LogCategory, message: string, data?: unknown) => write('warn', category, message, data),
  error: (category: LogCategory, message: string, data?: unknown) => write('error', category, message, data),

  init(): void {
    if (initialized) return;
    initialized = true;
    // Version must be the first log entry of every session.
    write('info', 'app', 'version', { version: APP_VERSION });
    store.init().catch(() => { /* swallow */ });
    installGlobalErrorHandlers();
    // Capture device/browser diagnostics once at startup so every session's
    // log carries them as a timestamped entry (the analyze-logs parser reads
    // this, and download() also mirrors it into the file header).
    collectSystemInfo()
      .then((system) => write('info', 'app', 'session info', system))
      .catch(() => { /* swallow */ });
    setInterval(() => { store.prune().catch(() => { /* swallow */ }); }, PRUNE_INTERVAL_MS);
  },

  async download(): Promise<void> {
    try {
      const since = Date.now() - RETENTION_MS;
      const entries = await store.getAllSince(since);
      // Record that a manual log download happened. getAllSince has already run,
      // so persisting now adds the marker to *this* export exactly once (via the
      // push below) and makes it visible in any future export (via the store).
      const marker: LogEntry = { ts: Date.now(), level: 'info', category: 'app', message: 'logs downloaded' };
      entries.push(marker);
      store.append(marker);
      const user = useUserStore.getState().user;
      let system: SystemInfo | undefined;
      try {
        system = await collectSystemInfo();
      } catch {
        // header system block is best-effort
      }
      const text = buildFile(entries, {
        generatedAt: Date.now(),
        env: environment.production ? 'prod' : 'dev',
        appVersion: APP_VERSION,
        userEmail: user?.email,
        userAgent: navigator.userAgent,
        system,
      });
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `songhunt-logs-${fileStamp(new Date())}.log`;
      document.body.appendChild(a);
      a.click();
      // Defer cleanup: revoking synchronously can cancel the download in some browsers.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch {
      // swallow
    }
  },
};
